import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { api, resolveImg } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" });

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    document.title = "Blog | Distópica — Indumentaria & Merch corporativo";
    api.get("/blog/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  return (
    <main className="bg-[#0A0A0A] min-h-screen">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight leading-none" data-testid="blog-logo">
            DISTÓPICA<span style={{ color: "var(--fuchsia)" }}>.</span>
          </Link>
          <Link
            to="/"
            data-testid="blog-back-home"
            className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-[var(--yellow)] transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={16} /> Volver al inicio
          </Link>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <span className="patch text-[var(--fuchsia)] -rotate-2">Blog / Recursos</span>
        <h1 className="font-display uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl mt-5 mb-6">
          Ideas para vestir tu marca
        </h1>
        <p className="text-lg text-neutral-400 max-w-2xl mb-16">
          Guías, tendencias y consejos sobre indumentaria corporativa, merchandising, DTF y bordado.
        </p>

        {posts === null ? (
          <p className="font-mono-x text-sm text-neutral-500">Cargando artículos…</p>
        ) : posts.length === 0 ? (
          <p className="font-mono-x text-sm text-neutral-500" data-testid="blog-empty">Todavía no hay artículos publicados.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease, delay: i * 0.08 }}
              >
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${i}`}
                  className="group block border border-white/10 h-full hover:border-[var(--yellow)]/50 transition-colors"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={resolveImg(p.cover_image) || "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/605ad99cca3584bc1202cb07a20ad8dd934dce8d88cb2469ddaef7f158b7113e.jpeg"}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex flex-col gap-3">
                    <div className="flex flex-wrap gap-2">
                      {(p.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="patch text-neutral-400 rotate-[-1deg]">{t}</span>
                      ))}
                    </div>
                    <h2 className="font-display uppercase text-xl leading-tight tracking-tight group-hover:text-[var(--yellow)] transition-colors">
                      {p.title}
                    </h2>
                    <p className="text-sm text-neutral-400 leading-relaxed line-clamp-3">{p.excerpt}</p>
                    <div className="flex items-center justify-between mt-2 pt-3 border-t border-white/10">
                      <span className="font-mono-x text-[11px] uppercase tracking-widest text-neutral-500">{fmtDate(p.created_at)}</span>
                      <ArrowUpRight size={16} className="text-neutral-500 group-hover:text-[var(--fuchsia)] group-hover:rotate-45 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
